import React, { useMemo, useRef, useState } from 'react';
import CalendarIcon from '../icons/CalendarIcon';
import Button from './Button';

export interface NoticeItem {
  id: string;
  content: string;
  startDate: string;
  endDate: string;
  status: '진행중' | '완료';
  createdAt?: Date | null;
  createdBy?: string;
  createdByName?: string;
}

export interface NoticeFormValues {
  content: string;
  startDate: string;
  endDate: string;
  status: '진행중' | '완료';
}

interface AddNewNoticeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  notices: NoticeItem[];
  onAddNotice: (notice: NoticeFormValues) => Promise<void> | void;
  onDeleteNotices: (noticeIds: string[]) => Promise<void> | void;
}

const formatDisplayDate = (value: string) => value.replace(/-/g, '.');

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '8px',
  border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
  fontSize: '14px',
  fontFamily: 'Pretendard, sans-serif',
  boxSizing: 'border-box',
  outline: 'none',
  background: 'var(--Color-fill-white, #FFFFFF)'
};

const buttonBaseStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 500,
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
};

const AddNewNoticeDialog: React.FC<AddNewNoticeDialogProps> = ({ isOpen, onClose, notices, onAddNotice, onDeleteNotices }) => {
  const [content, setContent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const startDateInputRef = useRef<HTMLInputElement | null>(null);
  const endDateInputRef = useRef<HTMLInputElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedNoticeIds, setSelectedNoticeIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isSubmitDisabled = useMemo(() => {
    return !content.trim() || !startDate;
  }, [content, startDate]);

  const isAllSelected = useMemo(() => {
    if (notices.length === 0) return false;
    return selectedNoticeIds.length === notices.length;
  }, [notices, selectedNoticeIds]);

  const handleToggleNotice = (noticeId: string) => {
    setSelectedNoticeIds((prev) =>
      prev.includes(noticeId)
        ? prev.filter((id) => id !== noticeId)
        : [...prev, noticeId]
    );
  };

  const handleToggleAllNotices = () => {
    if (isAllSelected) {
      setSelectedNoticeIds([]);
    } else {
      setSelectedNoticeIds(notices.map((notice) => notice.id));
    }
  };

  const handleDeleteSelectedNotices = async () => {
    if (selectedNoticeIds.length === 0 || isDeleting) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await Promise.resolve(onDeleteNotices(selectedNoticeIds));
      setSelectedNoticeIds([]);
    } catch (error) {
      console.error('선택된 알림 삭제 중 오류:', error);
      setDeleteError('선택한 알림을 삭제하지 못했습니다. 다시 시도해주세요.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!content.trim()) {
      setSubmitError('알림 내용을 입력해주세요.');
      return;
    }

    if (!startDate) {
      setSubmitError('노출 시작일을 선택해주세요.');
      return;
    }

    const finalEndDate = endDate || startDate;
    const displayStartDate = formatDisplayDate(startDate);
    const displayEndDate = formatDisplayDate(finalEndDate);

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await Promise.resolve(
        onAddNotice({
          content: content.trim(),
          startDate: displayStartDate,
          endDate: displayEndDate,
          status: '진행중'
        })
      );

      setContent('');
      setStartDate('');
      setEndDate('');
      onClose();
    } catch (error) {
      console.error('알림 등록 중 오류:', error);
      setSubmitError('알림 등록에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px'
      }}
    >
      <div
        style={{
          width: '43rem',
          height: '42.0625rem',
          padding: '24px 28px',
          background: 'var(--Color-background-normal, white)',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          overflow: 'hidden'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
            <div
              style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '20px',
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: 600,
                lineHeight: '30px'
              }}
            >
              알림 등록하기
            </div>
            <div
              style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '16px',
                fontFamily: 'Pretendard, sans-serif',
                fontWeight: 500,
                lineHeight: '24px'
              }}
            >
              알림 등록시, 해당 내용이 학생들의 알림 창에 노출 됩니다.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '4px',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                width: '24px',
                height: '24px',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--Color-text-strong, #0D0D0D)',
                fontSize: '20px',
                fontWeight: 600
              }}
            >
              ×
            </span>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'hidden', flex: 1 }}>
          <div
            style={{
              width: '39.5rem',
              height: '15.5625rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              background: 'var(--Color-background-sub, #F8F8F8)',
              borderRadius: '8px',
              padding: '16px 20px'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                flex: 1,
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: 600,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                알림 내역
                <Button
                  variant="outline"
                  size="small"
                  onClick={handleDeleteSelectedNotices}
                  disabled={selectedNoticeIds.length === 0 || isDeleting}
                >
                  {isDeleting ? '삭제 중...' : '선택 삭제'}
                </Button>
              </div>
              <div
                style={{
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  padding: '0.75rem 1.75rem',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <div
                  style={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: '1.5rem 3.8125rem 1fr 11.375rem 3.5rem',
                    columnGap: '1rem',
                    alignItems: 'center',
                    color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: 500
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleToggleAllNotices}
                    style={{ cursor: 'pointer' }}
                    aria-label="전체 선택"
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    No
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    알림 내용
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    노출 기간
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    상태
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  overflowY: 'auto',
                  maxHeight: '15.75rem'
                }}
              >
                {notices.length === 0 ? (
                  <div
                    style={{
                      padding: '24px',
                      textAlign: 'center',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontFamily: 'Pretendard, sans-serif',
                      fontSize: '14px'
                    }}
                  >
                    등록된 알림이 없습니다.
                  </div>
                ) : (
                  notices.map((notice, index) => (
                    <div
                      key={notice.id}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1.75rem',
                        display: 'flex',
                        justifyContent: 'center',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '14px',
                        fontFamily: 'Pretendard, sans-serif'
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          display: 'grid',
                          gridTemplateColumns: '1.5rem 3.8125rem 1fr 11.375rem 3.5rem',
                          columnGap: '1rem',
                          alignItems: 'center'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedNoticeIds.includes(notice.id)}
                          onChange={() => handleToggleNotice(notice.id)}
                          style={{ cursor: 'pointer' }}
                          aria-label={`${notice.content} 선택`}
                        />
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 400,
                            lineHeight: '1.3125rem'
                          }}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {notice.content}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: '#4C4C4C',
                            fontWeight: 400,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {notice.startDate} ~ {notice.endDate}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              padding: '0.125rem 0.75rem',
                              borderRadius: '8px',
                              border: notice.status === '진행중' ? 'none' : '1px solid var(--Color-line-normal, #F0F0F0)',
                              background: notice.status === '진행중' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-fill-white, white)',
                              color: notice.status === '진행중' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                              fontSize: '12px',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {notice.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              background: 'var(--Color-background-sub, #F8F8F8)',
              borderRadius: '8px',
              padding: '16px 20px'
            }}
          >
            <div
              style={{
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '12px'
              }}
            >
              <div
                style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '16px',
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: 600,
                  lineHeight: '24px'
                }}
              >
                알림 등록
              </div>
              <div
                style={{
                  width: '36.9375rem',
                  height: '5.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start'
                }}
              >
                <div
                  style={{
                    width: '100%',
                    flex: '1 1 0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      flex: '1 1 0',
                      padding: '12px 16px',
                      background: 'var(--Color-fill-white, #FFFFFF)',
                      borderRadius: '8px',
                      outline: '1.20px var(--Color-line-normal, #F0F0F0) solid',
                      outlineOffset: '-1.20px',
                      display: 'flex',
                      alignItems: 'flex-start'
                    }}
                  >
                    <textarea
                      value={content}
                      onChange={(event) => setContent(event.target.value)}
                      placeholder="내용을 입력해주세요."
                      style={{
                        flex: '1 1 0',
                        border: 'none',
                        resize: 'none',
                        background: 'transparent',
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: 500,
                        lineHeight: '19.5px',
                        outline: 'none',
                        height: '100%',
                        padding: 0
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                gap: '12px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '4px'
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--Color-text-assistive-strong, #4C4C4C)'
                  }}
                >
                  시작 날짜
                </span>
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <Button
                    width="7.75rem"
                    height="2.75rem"
                    variant="submit"
                    padding="0.75rem 1rem"
                    leftIcon={<CalendarIcon width={20} height={20} fill="#FFFFFF" />}
                    onClick={() => {
                      if (startDateInputRef.current) {
                        const input = startDateInputRef.current;
                        if (typeof (input as any).showPicker === 'function') {
                          (input as any).showPicker();
                        } else {
                          input.click();
                        }
                      }
                    }}
                  >
                    {startDate
                      ? formatDisplayDate(startDate)
                      : '날짜 선택'}
                  </Button>
                  <input
                    ref={startDateInputRef}
                    type="date"
                    value={startDate}
                    onChange={(event) => {
                      setStartDate(event.target.value);
                      if (!endDate) {
                        setEndDate(event.target.value);
                      }
                    }}
                    style={{
                      position: 'absolute',
                      top: '-0.5rem',
                      left: 0,
                      opacity: 0,
                      pointerEvents: 'none'
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '4px'
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--Color-text-assistive-strong, #4C4C4C)'
                  }}
                >
                  종료 날짜
                </span>
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <Button
                    width="7.75rem"
                    height="2.75rem"
                    variant="submit"
                    padding="0.75rem 1rem"
                    leftIcon={<CalendarIcon width={20} height={20} fill="#FFFFFF" />}
                    onClick={() => {
                      if (endDateInputRef.current) {
                        const input = endDateInputRef.current;
                        if (typeof (input as any).showPicker === 'function') {
                          (input as any).showPicker();
                        } else {
                          input.click();
                        }
                      }
                    }}
                  >
                    {endDate
                      ? formatDisplayDate(endDate)
                      : '날짜 선택'}
                  </Button>
                  <input
                    ref={endDateInputRef}
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    style={{
                      position: 'absolute',
                      top: '-0.5rem',
                      left: 0,
                      opacity: 0,
                      pointerEvents: 'none'
                    }}
                  />
                </div>
              </div>
              <Button
                type="button"
                width="16.375rem"
                height="2.75rem"
                variant="primary"
                disabled={isSubmitDisabled || isSubmitting}
                onClick={handleSubmit}
                padding="0.75rem 1rem"
              >
                {isSubmitting ? '등록 중...' : '등록'}
              </Button>
            </div>
          </div>
        </div>
        {deleteError && (
          <div
            style={{
              alignSelf: 'stretch',
              color: 'var(--Color-status-negative, #FF3838)',
              fontSize: '0.75rem',
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: 500,
              textAlign: 'right'
            }}
          >
            {deleteError}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddNewNoticeDialog;
